import { AxiosResponse } from "axios";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { axiosRestInstance } from "../../../../common/axios";
import { mapPhasesApiToUi } from "../../../slices/technology-settings/phases/phaseMapper";
import { mapIntegrationDetailApiToUi } from "../../../slices/technology-settings/technologies/integrationMapper";
import { mapTechnologiesApiToUi } from "../../../slices/technology-settings/technologies/technologyMapper";
import { technologySettingsTechnologiesSliceAction } from "../../../slices/technology-settings/technologies";
import type { GetPhasesApiResponse } from "../../../slices/technology-settings/phases/Types";
import type {
  CreateIntegrationApiResponse,
  CreateIntegrationSagaPayloadType,
  CreateTechnologyApiResponse,
  CreateTechnologySagaPayloadType,
  DeleteTechnologyApiResponse,
  DeleteTechnologySagaPayloadType,
  GetIntegrationByIdApiResponse,
  GetIntegrationByIdSagaPayloadType,
  GetTechnologiesApiResponse,
  IntegrationPayloadType,
  TechnologyPayloadType,
  UpdateIntegrationApiResponse,
  UpdateIntegrationSagaPayloadType,
  UpdateTechnologyApiResponse,
  UpdateTechnologySagaPayloadType,
} from "../../../slices/technology-settings/technologies/Types";
import type { RootStateType } from "../../../index";
import {
  getTechnologiesSagaAction,
  technologySettingsTechnologiesSagaActionTypes,
} from "./saga-actions";

const getTechnologiesAPI = async () => {
  const response = await axiosRestInstance.get<
    AxiosResponse<GetTechnologiesApiResponse>
  >("/technology-settings/technology/getData");
  return response.data;
};

const createTechnologyAPI = async (payload: TechnologyPayloadType) => {
  const response = await axiosRestInstance.post<
    AxiosResponse<CreateTechnologyApiResponse>
  >("/technology-settings/technology/insert", payload);
  return response.data;
};

const updateTechnologyAPI = async (
  id: string | number,
  payload: TechnologyPayloadType
) => {
  const response = await axiosRestInstance.put<
    AxiosResponse<UpdateTechnologyApiResponse>
  >(`/technology-settings/technology/update/${id}`, payload);
  return response.data;
};

const deleteTechnologyAPI = async (id: string | number) => {
  const response = await axiosRestInstance.delete<
    AxiosResponse<DeleteTechnologyApiResponse>
  >(`/technology-settings/technology/delete/${id}`);
  return response.data;
};

const getPhasesAPI = async () => {
  const response = await axiosRestInstance.get<
    AxiosResponse<GetPhasesApiResponse>
  >("/technology-settings/phase/getData");
  return response.data;
};

const getIntegrationByIdAPI = async (id: string | number) => {
  const response = await axiosRestInstance.get<
    AxiosResponse<GetIntegrationByIdApiResponse>
  >(`/technology-settings/integration/getData/${id}`);
  return response.data;
};

const createIntegrationAPI = async (
  technologyId: string | number,
  payload: IntegrationPayloadType
) => {
  const response = await axiosRestInstance.post<
    AxiosResponse<CreateIntegrationApiResponse>
  >(`/technology-settings/${technologyId}/integration/insert`, payload);
  return response.data;
};

const updateIntegrationAPI = async (
  id: string | number,
  payload: IntegrationPayloadType
) => {
  const response = await axiosRestInstance.put<
    AxiosResponse<UpdateIntegrationApiResponse>
  >(`/technology-settings/integration/update/${id}`, payload);
  return response.data;
};

function* getTechnologiesSaga() {
  try {
    yield put(
      technologySettingsTechnologiesSliceAction.setTechnologiesLoading(true)
    );
    const response: GetTechnologiesApiResponse = yield call(getTechnologiesAPI);
    if (response.success) {
      yield put(
        technologySettingsTechnologiesSliceAction.setTechnologies(
          mapTechnologiesApiToUi(response.data || [])
        )
      );
    } else {
      yield put(technologySettingsTechnologiesSliceAction.setTechnologies([]));
    }
  } catch (e) {
    yield put(technologySettingsTechnologiesSliceAction.setTechnologies([]));
    console.log(e);
  } finally {
    yield put(
      technologySettingsTechnologiesSliceAction.setTechnologiesLoading(false)
    );
  }
}

function* createTechnologySaga(action: {
  type: string;
  payload: CreateTechnologySagaPayloadType;
}) {
  try {
    yield put(
      technologySettingsTechnologiesSliceAction.setCreateTechnologyLoading(true)
    );
    const response: CreateTechnologyApiResponse = yield call(
      createTechnologyAPI,
      action.payload.data
    );
    if (response.success) {
      yield put(getTechnologiesSagaAction());
      if (action.payload.onSuccess) {
        action.payload.onSuccess(
          response.message || "Technology created successfully."
        );
      }
    } else if (action.payload.onError) {
      action.payload.onError(
        response.message || "Unable to create technology."
      );
    }
  } catch (e: any) {
    if (action.payload.onError) {
      action.payload.onError(
        e?.response?.data?.message || "Unable to create technology."
      );
    }
    console.log(e);
  } finally {
    yield put(
      technologySettingsTechnologiesSliceAction.setCreateTechnologyLoading(false)
    );
  }
}

function* updateTechnologySaga(action: {
  type: string;
  payload: UpdateTechnologySagaPayloadType;
}) {
  try {
    yield put(
      technologySettingsTechnologiesSliceAction.setUpdateTechnologyLoading(true)
    );
    const response: UpdateTechnologyApiResponse = yield call(
      updateTechnologyAPI,
      action.payload.id,
      action.payload.data
    );
    if (response.success) {
      yield put(getTechnologiesSagaAction());
      if (action.payload.onSuccess) {
        action.payload.onSuccess(
          response.message || "Technology updated successfully."
        );
      }
    } else if (action.payload.onError) {
      action.payload.onError(
        response.message || "Unable to update technology."
      );
    }
  } catch (e: any) {
    if (action.payload.onError) {
      action.payload.onError(
        e?.response?.data?.message || "Unable to update technology."
      );
    }
    console.log(e);
  } finally {
    yield put(
      technologySettingsTechnologiesSliceAction.setUpdateTechnologyLoading(false)
    );
  }
}

function* deleteTechnologySaga(action: {
  type: string;
  payload: DeleteTechnologySagaPayloadType;
}) {
  try {
    yield put(
      technologySettingsTechnologiesSliceAction.setDeleteTechnologyLoading(true)
    );
    const response: DeleteTechnologyApiResponse = yield call(
      deleteTechnologyAPI,
      action.payload.id
    );
    if (response.success) {
      yield put(getTechnologiesSagaAction());
      if (action.payload.onSuccess) {
        action.payload.onSuccess(
          response.message || "Technology deleted successfully."
        );
      }
    } else if (action.payload.onError) {
      action.payload.onError(
        response.message || "Unable to delete technology."
      );
    }
  } catch (e: any) {
    if (action.payload.onError) {
      action.payload.onError(
        e?.response?.data?.message || "Unable to delete technology."
      );
    }
    console.log(e);
  } finally {
    yield put(
      technologySettingsTechnologiesSliceAction.setDeleteTechnologyLoading(false)
    );
  }
}

function* getIntegrationByIdSaga(action: {
  type: string;
  payload: GetIntegrationByIdSagaPayloadType;
}) {
  try {
    yield put(
      technologySettingsTechnologiesSliceAction.setIntegrationDetailLoading(true)
    );
    yield put(technologySettingsTechnologiesSliceAction.setIntegrationDetail(null));

    let phases = (yield select(
      (state: RootStateType) => state.technology_settings_phases.phases
    )) as RootStateType["technology_settings_phases"]["phases"];

    if (!phases.length) {
      const phasesResponse: GetPhasesApiResponse = yield call(getPhasesAPI);
      if (phasesResponse.success) {
        phases = mapPhasesApiToUi(phasesResponse.data || []);
      }
    }

    const response: GetIntegrationByIdApiResponse = yield call(
      getIntegrationByIdAPI,
      action.payload.id
    );

    if (response.success && response.data) {
      yield put(
        technologySettingsTechnologiesSliceAction.setIntegrationDetail(
          mapIntegrationDetailApiToUi(response.data, phases)
        )
      );
      if (action.payload.onSuccess) {
        action.payload.onSuccess(
          response.message || "Integration details fetched successfully."
        );
      }
    } else if (action.payload.onError) {
      action.payload.onError(
        response.message || "Unable to fetch integration details."
      );
    }
  } catch (e: any) {
    if (action.payload.onError) {
      action.payload.onError(
        e?.response?.data?.message || "Unable to fetch integration details."
      );
    }
    console.log(e);
  } finally {
    yield put(
      technologySettingsTechnologiesSliceAction.setIntegrationDetailLoading(false)
    );
  }
}

function* createIntegrationSaga(action: {
  type: string;
  payload: CreateIntegrationSagaPayloadType;
}) {
  try {
    yield put(
      technologySettingsTechnologiesSliceAction.setCreateIntegrationLoading(true)
    );
    const response: CreateIntegrationApiResponse = yield call(
      createIntegrationAPI,
      action.payload.technologyId,
      action.payload.data
    );
    if (response.success) {
      yield put(getTechnologiesSagaAction());
      if (action.payload.onSuccess) {
        action.payload.onSuccess(
          response.message || "Integration created successfully."
        );
      }
    } else if (action.payload.onError) {
      action.payload.onError(
        response.message || "Unable to create integration."
      );
    }
  } catch (e: any) {
    if (action.payload.onError) {
      action.payload.onError(
        e?.response?.data?.message || "Unable to create integration."
      );
    }
    console.log(e);
  } finally {
    yield put(
      technologySettingsTechnologiesSliceAction.setCreateIntegrationLoading(false)
    );
  }
}

function* updateIntegrationSaga(action: {
  type: string;
  payload: UpdateIntegrationSagaPayloadType;
}) {
  try {
    yield put(
      technologySettingsTechnologiesSliceAction.setUpdateIntegrationLoading(true)
    );
    const response: UpdateIntegrationApiResponse = yield call(
      updateIntegrationAPI,
      action.payload.id,
      action.payload.data
    );
    if (response.success) {
      yield put(getTechnologiesSagaAction());
      if (action.payload.onSuccess) {
        action.payload.onSuccess(
          response.message || "Integration updated successfully."
        );
      }
    } else if (action.payload.onError) {
      action.payload.onError(
        response.message || "Unable to update integration."
      );
    }
  } catch (e: any) {
    if (action.payload.onError) {
      action.payload.onError(
        e?.response?.data?.message || "Unable to update integration."
      );
    }
    console.log(e);
  } finally {
    yield put(
      technologySettingsTechnologiesSliceAction.setUpdateIntegrationLoading(false)
    );
  }
}

export default function* technologySettingsTechnologiesSaga() {
  yield takeLatest(
    technologySettingsTechnologiesSagaActionTypes.callGetTechnologiesSaga,
    getTechnologiesSaga
  );
  yield takeLatest(
    technologySettingsTechnologiesSagaActionTypes.callCreateTechnologySaga,
    createTechnologySaga
  );
  yield takeLatest(
    technologySettingsTechnologiesSagaActionTypes.callUpdateTechnologySaga,
    updateTechnologySaga
  );
  yield takeLatest(
    technologySettingsTechnologiesSagaActionTypes.callDeleteTechnologySaga,
    deleteTechnologySaga
  );
  yield takeLatest(
    technologySettingsTechnologiesSagaActionTypes.callGetIntegrationByIdSaga,
    getIntegrationByIdSaga
  );
  yield takeLatest(
    technologySettingsTechnologiesSagaActionTypes.callCreateIntegrationSaga,
    createIntegrationSaga
  );
  yield takeLatest(
    technologySettingsTechnologiesSagaActionTypes.callUpdateIntegrationSaga,
    updateIntegrationSaga
  );
}
